// Blog embed: renders the verifier's figures into placeholder elements on a
// page that includes this module, e.g.
//
//   <div data-ari="media"></div>
//   <div data-ari="hashtree" data-step="2"></div>
//   <div data-ari="dropzone"></div>
//   <script type="module" src="/apple-reference-image-verifier/embed.js"></script>
//
// Each placeholder gets its own shadow root with components.css, so the host
// page's styles and the verifier's never meet. The figures first show the
// bundled example photo (from its prebuilt report.json and preview images);
// a "dropzone" placeholder lets the reader verify their own photo, after which
// every figure on the page shows that photo instead. Nothing leaves the
// browser.
import { renderIosUploadHelp } from "./render/iosUploadHelp.js";
import { h, fmtBytes } from "./render/h.js";
import { renderVerdict } from "./render/verdict.js";
import { renderFileMap } from "./render/fileMap.js";
import { renderByteMap } from "./render/byteMap.js";
import { renderHashTree } from "./render/hashTree.js";
import { renderBoxAnatomy, renderTimestamps } from "./render/boxDetail.js";
import { renderCertChain } from "./render/certChain.js";
import { renderMedia, displayableUrl } from "./render/media.js";
import { analyse, errorReport, fetchPhoto } from "./pipeline.js";

const asset = (path) => new URL(path, import.meta.url).href;
const EXAMPLE = {
  name: "IMG_0104.jpeg",
  report: asset("assets/samples/IMG_0104.report.json"),
  photo: asset("assets/samples/IMG_0104.jpeg"),
  preview: asset("assets/samples/IMG_0104.preview.jpg"),
  size: "12.5 MB",
};

// Figures that need the parsed reference image fall back to the example when
// the reader's photo has none.
const RENDERERS = {
  verification: {
    render: (s) =>
      h(
        "div.verification-panel",
        s.media &&
          renderMedia({
            ...s.media,
            certified: s.report.verdict.status === "pass",
          }),
        renderVerdict(s.report),
      ),
  },
  media: {
    render: (s) =>
      renderMedia({
        ...s.media,
        certified: s.report.verdict.status === "pass",
      }),
    needsMedia: true,
  },
  verdict: { render: (s) => renderVerdict(s.report) },
  filemap: { render: (s) => renderFileMap(s.report), needsProcessed: true },
  bytemap: {
    render: (s, host) =>
      renderByteMap(s.report, { tableOpen: host.dataset.table === "open" }),
    needsProcessed: true,
  },
  hashtree: {
    render: (s, host) =>
      renderHashTree(s.report, { step: Number(host.dataset.step || 3) }),
    needsProcessed: true,
  },
  box: { render: (s) => renderBoxAnatomy(s.report), needsProcessed: true },
  timestamps: {
    render: (s) => renderTimestamps(s.report),
    needsProcessed: true,
  },
  certs: { render: (s) => renderCertChain(s.report), needsProcessed: true },
  dropzone: { render: (s, host, ctx) => renderDropzone(s, ctx) },
};

let example = null; // { report, media, label }
let current = null; // the reader's photo, once analysed
let previewUrls = [];
let mediaGeneration = 0;
const hosts = [];

async function componentStyles() {
  const css = await fetch(asset("components.css")).then((r) => r.text());
  if (!("adoptedStyleSheets" in Document.prototype)) return { css };
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(css);
  return { sheet };
}

function mount(host, styles) {
  const root = host.attachShadow({ mode: "open" });
  if (styles.sheet) root.adoptedStyleSheets = [styles.sheet];
  else root.append(h("style", styles.css));
  const box = h("div.embed");
  root.append(box);
  return { host, kind: host.dataset.ari, box };
}

function stateFor(kind) {
  const r = RENDERERS[kind];
  if (!current) return { state: example, fallback: false };
  const missing =
    (r.needsProcessed && !current.report.processed) ||
    (r.needsMedia && !current.media);
  return missing ? { state: example, fallback: true } : { state: current };
}

// The drop zone keeps its state (status line, chosen file) across re-renders.
function renderAll() {
  for (const slot of hosts)
    if (slot.kind !== "dropzone" || !slot.rendered) renderOne(slot);
}

function renderOne(slot) {
  const r = RENDERERS[slot.kind];
  if (!r) {
    slot.box.replaceChildren(
      h("p.embed-error", `Unknown figure "${slot.kind}".`),
    );
    return;
  }
  const { state, fallback } = stateFor(slot.kind);
  if (!state) return;
  try {
    const node = r.render(state, slot.host, { slot });
    const parts = [node];
    if (slot.kind !== "dropzone" && (current || fallback))
      parts.push(sourceLine(state, fallback));
    slot.box.replaceChildren(...parts);
    slot.rendered = true;
  } catch (e) {
    console.error(`Could not render ${slot.kind}`, e);
    slot.box.replaceChildren(
      h("p.embed-error", `Could not render this figure: ${e.message || e}`),
    );
  }
}

function sourceLine(state, fallback) {
  if (state === example && !current) {
    return h("p.embed-source", `Example photo ${EXAMPLE.name}.`);
  }
  if (fallback) {
    return h(
      "p.embed-source",
      `${current.label} carries no reference image, so this figure still shows the example photo ${EXAMPLE.name}.`,
    );
  }
  return h(
    "p.embed-source",
    `${state.label}, analysed in your browser. `,
    h(
      "button",
      { type: "button", onclick: resetToExample },
      "Back to the example",
    ),
  );
}

function resetToExample() {
  current = null;
  previewUrls.forEach((u) => URL.revokeObjectURL(u));
  previewUrls = [];
  mediaGeneration++;
  renderAll();
}

// --- the reader's own photo ---

function mediaSources(processed, file) {
  const generation = ++mediaGeneration;
  const track = (u) => (previewUrls.push(u), u);
  return {
    outer: displayableUrl(file, track, () => generation === mediaGeneration),
    inner: processed
      ? track(
          URL.createObjectURL(new Blob([processed], { type: "image/jpeg" })),
        )
      : null,
  };
}

async function handleFile(file, setStatus) {
  previewUrls.forEach((u) => URL.revokeObjectURL(u));
  previewUrls = [];
  try {
    const { report, processedJpeg } = await analyse(file, (step) =>
      setStatus(`${step} ${file.name} (${fmtBytes(file.size)})…`),
    );
    current = {
      report,
      media: report.file?.container ? mediaSources(processedJpeg, file) : null,
      label: `Your photo ${file.name}`,
    };
    const v = report.verdict.status;
    if (v === "pass" || v === "fail") {
      setStatus("", false);
    } else if (v === "not-reference-image") {
      setStatus(
        `${file.name} carries no Reference Image data. The verdict below says what was found; the other figures keep showing the example.`,
        false,
      );
    } else {
      setStatus(
        report.verdict.reasons[0] || "Could not analyse this file.",
        false,
      );
    }
  } catch (e) {
    console.error("Analysis failed", e);
    current = {
      report: errorReport(file, `Unexpected error: ${e.message || e}`),
      media: null,
      label: `Your photo ${file.name}`,
    };
    setStatus(`Could not analyse ${file.name}: ${e.message || e}`, false);
  }
  renderAll();
}

function renderDropzone() {
  const input = h("input", {
    type: "file",
    accept: ".heic,.heif,.jpg,.jpeg,image/heic,image/heif,image/jpeg",
    hidden: true,
  });
  const status = h("p.status", { hidden: true });
  const setStatus = (text, busy = true) => {
    status.textContent = text;
    status.classList.toggle("busy", busy);
    status.hidden = !text;
  };
  const example = h(
    "button.linklike",
    {
      type: "button",
      onclick: async () => {
        example.classList.add("loading");
        example.disabled = true;
        setStatus(`Fetching the example photo (${EXAMPLE.size})…`);
        try {
          await handleFile(
            await fetchPhoto(EXAMPLE.photo, EXAMPLE.name),
            setStatus,
          );
        } catch (e) {
          setStatus(`Could not load the example: ${e.message}`, false);
        } finally {
          example.classList.remove("loading");
          example.disabled = false;
        }
      },
    },
    "verify the example photo live",
  );
  const choose = h("button.linklike", { type: "button" }, "choose a file");
  const drop = h(
    "div.drop",
    { tabindex: "0", role: "button", "aria-label": "Choose or drop a photo" },
    input,
    h(
      "div.drop-copy",
      h(
        "p.drop-main",
        "Drop an iPhone 18 Pro photo here ",
        h("span.drop-choose", "or ", choose),
      ),
      h("p.drop-formats", "HEIC, HEIF or JPEG"),
    ),
    h(
      "div.drop-example",
      h(
        "button.example-thumbnail",
        {
          type: "button",
          "aria-label": "Verify the example photo",
          onclick: () => example.click(),
        },
        h("img", {
          src: EXAMPLE.preview,
          alt: "Example photo",
          width: 64,
          height: 72,
        }),
      ),
      h(
        "p.drop-sub",
        "or ",
        example,
        h("span.example-size", ` (${EXAMPLE.size})`),
      ),
    ),
  );
  const pick = () => input.click();
  choose.addEventListener("click", pick);
  input.addEventListener("change", () => {
    if (input.files[0]) handleFile(input.files[0], setStatus);
  });
  drop.addEventListener("click", (e) => {
    if (e.target.closest("button, label, a")) return;
    pick();
  });
  drop.addEventListener("keydown", (e) => {
    if (e.target !== drop) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      pick();
    }
  });
  const hasFiles = (e) => [...(e.dataTransfer?.types || [])].includes("Files");
  drop.addEventListener("dragover", (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    drop.classList.add("over");
  });
  drop.addEventListener("dragleave", () => drop.classList.remove("over"));
  drop.addEventListener("drop", (e) => {
    e.preventDefault();
    drop.classList.remove("over");
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f, setStatus);
  });
  return h(
    "div.embed-drop",
    drop,
    renderIosUploadHelp(),
    status,
    h(
      "p.privacy",
      "The photo is processed in your browser and never leaves your device.",
    ),
  );
}

// --- start ---

async function start() {
  const placeholders = [...document.querySelectorAll("[data-ari]")];
  if (!placeholders.length) return;
  const styles = await componentStyles();
  for (const el of placeholders) hosts.push(mount(el, styles));
  try {
    const report = await fetch(EXAMPLE.report).then((r) => {
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json();
    });
    example = {
      report,
      // Icons stand in for the photos until the reader verifies one.
      media: { placeholder: true },
      label: `Example photo ${EXAMPLE.name}`,
    };
  } catch (e) {
    console.error("Could not load the example report", e);
    for (const slot of hosts) {
      if (slot.kind === "dropzone") continue;
      slot.box.replaceChildren(
        h(
          "p.embed-error",
          `Could not load the example photo's report (${e.message || e}).`,
        ),
      );
    }
    // The drop zone still works without the example.
    for (const slot of hosts) if (slot.kind === "dropzone") renderOne(slot);
    return;
  }
  renderAll();
}

start();
